/**
 * src/contact/core/ContactFixedForm.js
 * -------------------------------
 * Reusable fixed form component that leverages existing ContactModal infrastructure
 * without the modal overlay functionality.
 *
 * Key Features:
 * - Reuses all existing validation, sanitization, and submission logic
 * - Works as an inline form on any page
 * - Maintains all security features and error handling
 * - Uses same FormSubmitter, FormValidator, and SafeDOM utilities
 * - Supports dataset-driven configuration like ContactModal
 * - Flash messages and toast notifications
 * - Analytics integration
 *
 * Usage:
 * ```javascript
 * import ContactFixedForm from './ContactFixedForm.js';
 * 
 * // Initialize on page load
 * const fixedForm = ContactFixedForm.create(namespace, {
 *   formSelector: '#contact-form-fixed',
 *   showSuccessMessage: true,
 *   redirectOnSuccess: '/thank-you'
 * });
 * ```
 */

import ErrorHandler from '../../utils/ErrorHandler.js';
import { FormValidator } from '../utils/FormValidator.js';
import { SafeDOM } from '../utils/SafeDOM.js';
import { FormSubmitter } from '../utils/FormSubmitter.js';
import { orgContactPhrase } from '../../utils/orgContact.js';

const isBrowser = SafeDOM.isBrowser;

export default class ContactFixedForm {
  constructor(namespace, options = {}) {
    this.namespace = namespace;
    this.form = null;
    this.submitButton = null;
    this.isSubmitting = false;
    this.submitTimeout = null;
    this.eventsBound = false;
    this.isBrowser = isBrowser;
    this.validationRules = {};

    // Configuration options
    this.options = {
      formSelector: '#contact-form-fixed',
      showSuccessMessage: true,
      showErrorMessage: true,
      resetFormOnSuccess: true,
      redirectOnSuccess: null,
      scrollToFormOnError: true,
      successMessageDuration: 5000,
      errorMessageDuration: 8000,
      enableAnalytics: true,
      debug: false,
      ...options
    };

    this.submitterOptions = {
      endpoints: ['/api/contact', '/contact'],
      debug: this.options.debug,
      analyticsHandler: this.handleAnalytics.bind(this),
      headers: {
        'Accept': 'application/json'
      },
      transformFields: {
        firstName: (value) => value.trim(),
        lastName: (value) => value.trim(),
        email: (value) => value.trim().toLowerCase(),
        phone: (value) => value.replace(/\s+/g, '').replace(/[^\d+()-]/g, ''),
        message: (value) => value.trim()
      },
      additionalData: {
        source: 'contact_fixed_form',
        type: 'contact_inquiry'
      },
      ...options.submitter
    };

    this.flashOptions = {
      position: 'top-form',
      duration: this.options.successMessageDuration,
      errorDuration: this.options.errorMessageDuration,
      showIcon: true,
      showCloseButton: true,
      stackMessages: false, // Don't stack for fixed forms
      ...options.flash
    };

    if (this.isBrowser) {
      this.errorHandler = new ErrorHandler({ module: 'ContactFixedForm', maxErrors: 15 });
      SafeDOM.init(this.errorHandler);
      this.eventManager = SafeDOM.createEventManager();
      this.formSubmitter = FormSubmitter.createContactSubmitter(this.submitterOptions);
      this._deferInit();
    }
  }

  _deferInit() {
    SafeDOM.waitForDOMReady(() => this.init());
  }

  init() {
    if (!this.isBrowser) return;
    
    try {
      // Find the form using the configured selector
      this.form = SafeDOM.safeQuerySelector(this.options.formSelector);
      
      if (!this.form) {
        throw new Error(`Fixed form not found with selector: ${this.options.formSelector}`);
      }

      this.submitButton = SafeDOM.safeQuerySelector('button[type="submit"]', this.form);

      this.loadValidationRules();

      if (!this.eventsBound) {
        this.bindEvents();
        this.eventsBound = true;
      }

      this.errorHandler?.logSuccess('ContactFixedForm initialized', { 
        category: 'initialization', 
        action: 'success',
        formSelector: this.options.formSelector,
        validationRules: this.validationRules
      });
    } catch (err) {
      this.errorHandler?.handleFatalError('Fixed form initialization failed', err, { 
        category: 'initialization', 
        action: 'failed',
        formSelector: this.options.formSelector
      });
    }
  }

  // Reuse validation rules loading from ContactModal
  loadValidationRules() {
    if (!this.form) return;

    const dataset = this.form.dataset;
    
    this.validationRules = {
      message: {
        minLength: parseInt(dataset.validationMessageMinLength) || 0,
        maxLength: parseInt(dataset.validationMessageMaxLength) || 2000,
        required: dataset.validationMessageRequired === 'true'
      },
      firstName: {
        minLength: parseInt(dataset.validationFirstnameMinLength) || 2,
        maxLength: parseInt(dataset.validationFirstnameMaxLength) || 50,
        required: true
      },
      lastName: {
        minLength: 0,
        maxLength: parseInt(dataset.validationLastnameMaxLength) || 50,
        required: false
      },
      email: {
        maxLength: parseInt(dataset.validationEmailMaxLength) || 254,
        required: true
      },
      phone: {
        required: dataset.validationPhoneRequired === 'true'
      }
    };

    if (this.options.debug) {
      console.log('📋 Fixed form validation rules loaded:', this.validationRules);
    }
  }

  bindEvents() {
    if (!this.isBrowser) return;
    this.removeEventListeners();

    // Inject reCAPTCHA script immediately so it's ready before the user submits
    const siteKey = this.form.dataset.recaptchaSiteKey;
    if (siteKey) this._injectRecaptchaScript(siteKey);

    // Bind form submission
    this.handleSubmitBound = e => this.handleSubmit(e);
    this.eventManager.add(this.form, 'submit', this.handleSubmitBound);

    // Bind field validation events
    SafeDOM.safeQuerySelectorAll('input, textarea', this.form)
      .forEach(field => {
        this.eventManager.add(field, 'blur', () => this.validateField(field));
        this.eventManager.add(field, 'input', () => this.clearFieldError(field));
      });
  }

  _injectRecaptchaScript(siteKey) {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    document.head.appendChild(script);
  }

  removeEventListeners() {
    if (!this.isBrowser) return;
    this.eventManager.cleanup();
  }

  // Reuse validation methods from ContactModal
  validateContactForm(form) {
    const errors = [];
    let valid = true;

    const formData = new FormData(form);
    const data = {
      firstName: formData.get('firstName')?.trim() || '',
      lastName: formData.get('lastName')?.trim() || '',
      email: formData.get('email')?.trim() || '',
      phone: formData.get('phone')?.trim() || '',
      message: formData.get('message')?.trim() || ''
    };

    for (const [fieldName, value] of Object.entries(data)) {
      const fieldValidation = this.validateFieldWithRules(fieldName, value);
      if (!fieldValidation.valid) {
        errors.push(fieldValidation.error);
        valid = false;
      }
    }

    return { valid, errors, message: valid ? '' : 'Please correct the errors below', data };
  }

  // Reuse field validation from ContactModal
  validateFieldWithRules(fieldName, value) {
    const rules = this.getFieldValidationRules(fieldName);
    const trimmedValue = value?.trim() || '';

    switch (fieldName) {
      case 'firstName':
        return this.validateNameField(fieldName, trimmedValue, rules, true);
      case 'lastName':
        return this.validateNameField(fieldName, trimmedValue, rules, false);
      case 'email':
        return this.validateEmailField(trimmedValue, rules);
      case 'phone':
        return this.validatePhoneField(trimmedValue, rules);
      case 'message':
        return this.validateMessageField(trimmedValue, rules);
      default:
        return { valid: true, error: null };
    }
  }

  getFieldValidationRules(fieldName) {
    return this.validationRules[fieldName] || {};
  }

  // Reuse validation helper methods from ContactModal
  validateNameField(fieldName, value, rules, forceRequired = false) {
    const required = forceRequired || rules.required;
    const minLength = rules.minLength || 0;
    const maxLength = rules.maxLength || 50;

    if (required && !value) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: `${fieldName === 'firstName' ? 'First name' : 'Last name'} is required` 
        }
      };
    }

    if (value && value.length < minLength) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: `${fieldName === 'firstName' ? 'First name' : 'Last name'} must be at least ${minLength} characters` 
        }
      };
    }

    if (value && value.length > maxLength) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: `${fieldName === 'firstName' ? 'First name' : 'Last name'} must be less than ${maxLength} characters` 
        }
      };
    }

    if (value && !/^[a-zA-Z\s\-']+$/.test(value)) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: `${fieldName === 'firstName' ? 'First name' : 'Last name'} can only contain letters, spaces, hyphens, and apostrophes` 
        }
      };
    }

    return { valid: true, error: null };
  }

  validateEmailField(value, rules) {
    if (!value) {
      return {
        valid: false,
        error: { field: 'email', message: 'Email is required' }
      };
    }

    if (!FormValidator.isValidEmail(value)) {
      return {
        valid: false,
        error: { field: 'email', message: 'Please enter a valid email address' }
      };
    }

    const maxLength = rules.maxLength || 254;
    if (value.length > maxLength) {
      return {
        valid: false,
        error: { field: 'email', message: `Email address must be less than ${maxLength} characters` }
      };
    }

    return { valid: true, error: null };
  }

  validatePhoneField(value, rules) {
    const required = rules.required || false;

    if (required && !value) {
      return {
        valid: false,
        error: { field: 'phone', message: 'Phone number is required' }
      };
    }

    if (value && !FormValidator.isValidPhone(value)) {
      return {
        valid: false,
        error: { field: 'phone', message: 'Please enter a valid phone number' }
      };
    }

    return { valid: true, error: null };
  }

  validateMessageField(value, rules) {
    const required = rules.required || false;
    const minLength = rules.minLength || 0;
    const maxLength = rules.maxLength || 2000;

    if (required && !value) {
      return {
        valid: false,
        error: { field: 'message', message: 'Message is required' }
      };
    }

    if (value && minLength > 0 && value.length < minLength) {
      return {
        valid: false,
        error: { 
          field: 'message', 
          message: `Message must be at least ${minLength} characters` 
        }
      };
    }

    if (value && value.length > maxLength) {
      return {
        valid: false,
        error: { 
          field: 'message', 
          message: `Message must be less than ${maxLength} characters` 
        }
      };
    }

    return { valid: true, error: null };
  }

  // Modified submit handler for fixed forms
  async handleSubmit(e) {
    e.preventDefault();
    
    if (!this.isBrowser || this.isSubmitting) return;

    try {
      this.isSubmitting = true;
      this.setLoading(true);
      this.clearAllFieldErrors();
      this.clearAllMessages();

      const validation = this.validateContactForm(this.form);
      if (!validation.valid) {
        this.showInlineError(validation.message);
        validation.errors.forEach(error => {
          const fieldElement = SafeDOM.safeQuerySelector(
            `input[name="${error.field}"], textarea[name="${error.field}"]`, 
            this.form
          );
          if (fieldElement) {
            this.showFieldError(fieldElement, error.message);
          }
        });

        if (this.options.scrollToFormOnError) {
          this.scrollToForm();
        }
        return;
      }

      // Fetch reCAPTCHA v3 token if the form has a site key configured
      const recaptchaToken = await this._getRecaptchaToken();

      // If reCAPTCHA is configured but token couldn't be obtained, block submission
      // and tell the user — don't silently submit without the token. Most often
      // caused by an ad-blocker/privacy extension blocking Google's script, so
      // give them a way to still reach us instead of a dead end, and keep the
      // message visible (duration 0) long enough to read and copy it.
      if (this.form?.dataset?.recaptchaSiteKey && !recaptchaToken) {
        this.showInlineError(
          'We couldn\'t complete the security check for this form — this is often caused by an ad-blocker or privacy extension blocking Google\'s verification script. Try disabling it and resubmitting, or reach us directly at ' + orgContactPhrase() + '.',
          0
        );
        this._reportClientBlock('recaptcha_blocked');
        return;
      }

      const result = await this.formSubmitter.submit(this.form, {
        additionalData: {
          source: 'contact_fixed_form',
          type: 'contact_inquiry',
          formInstance: this.constructor.name,
          timestamp: new Date().toISOString(),
          validationRules: this.validationRules,
          ...(recaptchaToken && { recaptchaToken })
        }
      });

      console.log('✅ Fixed form submission result:', result);
      this.handleSubmissionSuccess(result);

      this.errorHandler?.logSuccess('Fixed form submitted successfully', { 
        category: 'form-submission', 
        action: 'success',
        submissionId: result.submissionId
      });

    } catch (err) {
      console.error('❌ Fixed form submission error:', err);
      
      this.errorHandler?.handleFatalError('Fixed form submission failed', err, { 
        category: 'form-submission', 
        action: 'failed',
        errorCategory: err.category
      });
      
      this.handleSubmissionError(err);
      
    } finally {
      this.isSubmitting = false;
      this.setLoading(false);
    }
  }

  /**
   * Fetch a reCAPTCHA v3 token for the current form action.
   * Returns null if reCAPTCHA is not configured or the script hasn't loaded.
   * Never throws — a missing token is handled gracefully server-side.
   */
  async _getRecaptchaToken() {
    const siteKey = this.form?.dataset?.recaptchaSiteKey;
    if (!siteKey) return null;

    // Wait for the reCAPTCHA script to load if it's still in flight
    if (typeof grecaptcha === 'undefined') {
      const scriptInjected = !!document.querySelector('script[src*="recaptcha/api.js"]');
      if (!scriptInjected) {
        console.warn('[ContactFixedForm] reCAPTCHA script not injected');
        return null;
      }
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('reCAPTCHA load timeout')), 8000);
          const poll = setInterval(() => {
            if (typeof grecaptcha !== 'undefined') {
              clearInterval(poll);
              clearTimeout(timeout);
              resolve();
            }
          }, 50);
        });
      } catch {
        console.warn('[ContactFixedForm] reCAPTCHA did not load in time');
        return null;
      }
    }

    try {
      return await new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(siteKey, { action: 'contact_form' }).then(resolve).catch(reject);
        });
      });
    } catch (err) {
      console.warn('[ContactFixedForm] reCAPTCHA token fetch failed:', err.message);
      return null;
    }
  }

  /**
   * Best-effort, PII-free ping so a submission blocked entirely client-side
   * (before any fetch to the real endpoint) still leaves a server-side trace.
   * Never throws — this must not affect the user-facing error flow.
   */
  _reportClientBlock(reason) {
    try {
      if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
      const formId = this.form?.dataset?.formId || 'unknown';
      const payload = JSON.stringify({ formId, reason });
      navigator.sendBeacon('/api/track/contact-blocked', new Blob([payload], { type: 'application/json' }));
    } catch {
      // best-effort only
    }
  }

  handleSubmissionSuccess(result) {
    this.trackSubmissionEvent('success', { submissionId: result.submissionId });
    const responseData = result?.data || {};
    const message = responseData.message ||
      'Thank you! Your message has been sent successfully. We\'ll get back to you soon.';
    const messageType = responseData.flashType || 'success';

    if (this.options.showSuccessMessage) {
      this.showInlineMessage(message, messageType);
      // Scroll so the user can see the message
      this.scrollToForm();
    }

    if (this.options.resetFormOnSuccess) {
      // Reset the fields only — the success message stays until its own auto-dismiss timer fires
      setTimeout(() => {
        this.form?.reset();
        this.clearAllFieldErrors();
      }, 2000);
    }

    // Handle redirect if configured
    if (this.options.redirectOnSuccess) {
      setTimeout(() => {
        window.location.href = this.options.redirectOnSuccess;
      }, 3000);
    }
    
    this.errorHandler?.logSuccess('Fixed form submitted successfully with success message', { 
      category: 'form-submission', 
      action: 'success',
      submissionId: result.submissionId
    });
  }

  handleSubmissionError(error) {
    this.trackSubmissionEvent('error', { 
      errorCategory: error.category,
      errorMessage: error.message
    });
    
    let userMessage = error.userMessage || 'Sorry, there was an error sending your message. Please try again.';
    
    switch (error.category) {
      case 'duplicate':
        userMessage = 'This form was already submitted recently. Please wait before submitting again.';
        break;
      case 'validation':
        userMessage = 'Please check your information and try again.';
        break;
      case 'timeout':
        userMessage = 'The request timed out. Please check your connection and try again.';
        break;
      case 'server':
        userMessage = 'Our servers are experiencing issues. Please try again in a few minutes.';
        break;
    }
    
    if (this.options.showErrorMessage) {
      this.showInlineError(userMessage);
    }

    if (this.options.scrollToFormOnError) {
      this.scrollToForm();
    }
  }

  // Inline message methods (instead of flash messages)
  showInlineSuccess(message) {
    this.showInlineMessage(message, 'success');
  }

  showInlineInfo(message) {
    this.showInlineMessage(message, 'info');
  }

  showInlineWarning(message) {
    this.showInlineMessage(message, 'warning');
  }

  showInlineError(message, duration) {
    this.showInlineMessage(message, 'error', duration);
  }

  showInlineMessage(message, type = 'success', duration) {
    if (!this.isBrowser || !this.form) return;

    // Remove any existing message
    this.clearAllMessages();

    const messageContainer = SafeDOM.safeCreateElement('div', {
      className: `form-message ${type} form-message-${type} form-message-inline`,
      innerHTML: this.buildInlineMessageHTML(message, type),
      'data-message-type': type
    });

    if (!messageContainer) return;

    // Insert at the top of the form
    const firstChild = this.form.firstChild;
    if (firstChild) {
      this.form.insertBefore(messageContainer, firstChild);
    } else {
      SafeDOM.safeAppendChild(this.form, messageContainer);
    }

    // Add close button functionality
    const closeButton = messageContainer.querySelector('.message-close');
    if (closeButton) {
      this.eventManager.add(closeButton, 'click', () => {
        this.hideInlineMessage(messageContainer);
      });
    }

    // Auto-hide — `duration` overrides the type default when explicitly passed;
    // 0 means persist until manually dismissed (e.g. a dead-end error with a
    // fallback contact method the visitor needs time to read and copy).
    const defaultDuration = type === 'success' ? this.options.successMessageDuration : this.options.errorMessageDuration;
    const resolvedDuration = duration !== undefined ? duration : defaultDuration;
    if ((type === 'success' || type === 'error') && resolvedDuration > 0) {
      setTimeout(() => {
        this.hideInlineMessage(messageContainer);
      }, resolvedDuration);
    }

    this.errorHandler?.logSuccess('Inline message displayed', { 
      category: 'inline-message', 
      action: 'show',
      type,
      message: message.substring(0, 100)
    });

    return messageContainer;
  }

  buildInlineMessageHTML(message, type) {
    const icon = this.getMessageIcon(type);
    const closeButton = '<button class="message-close" aria-label="Close message" type="button">&times;</button>';

    return `
      <div class="message-content">
        <span class="message-icon" aria-hidden="true">${icon}</span>
        <span class="message-text">${this.escapeHTML(message)}</span>
        ${closeButton}
      </div>
    `;
  }

  hideInlineMessage(messageElement) {
    if (!messageElement || !this.isBrowser) return;
    SafeDOM.safeRemoveElement(messageElement);
  }

  clearAllMessages() {
    if (!this.isBrowser || !this.form) return;
    
    SafeDOM.safeQuerySelectorAll('.form-message-inline', this.form).forEach(msg => {
      SafeDOM.safeRemoveElement(msg);
    });
  }

  getMessageIcon(type) {
    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ'
    };
    return icons[type] || icons.info;
  }

  escapeHTML(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  scrollToForm() {
    if (!this.isBrowser || !this.form) return;
    
    this.form.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }

  // Reuse field error methods from ContactModal
  validateField(field) {
    if (!field) return false;

    const value = field.value?.trim();
    const name = field.name;
    
    const validation = this.validateFieldWithRules(name, value);
    
    if (!validation.valid) {
      this.showFieldError(field, validation.error.message);
      return false;
    } else {
      this.clearFieldError(field);
      return true;
    }
  }

  showFieldError(field, msg) {
    if (!this.isBrowser || !field) return;
    
    if (typeof field === 'string') {
      field = SafeDOM.safeQuerySelector(`input[name="${field}"], textarea[name="${field}"]`, this.form);
      if (!field) return;
    }
    
    this.clearFieldError(field);
    
    const errorEl = SafeDOM.safeCreateElement('div', {
      className: 'field-error',
      textContent: msg,
      styles: {
        color: '#dc3545',
        fontSize: '14px',
        marginTop: '4px'
      }
    });
    
    if (errorEl) {
      SafeDOM.safeSetStyle(field, { borderColor: '#dc3545' });
      SafeDOM.safeAppendChild(field.parentNode, errorEl);
    }
  }

  clearFieldError(field) {
    if (!this.isBrowser || !field) return;

    SafeDOM.safeSetStyle(field, { borderColor: '' });
    const errorEl = SafeDOM.safeQuerySelector('.field-error', field.parentNode);
    if (errorEl) {
      SafeDOM.safeRemoveElement(errorEl);
    }
  }

  clearAllFieldErrors() {
    if (!this.isBrowser || !this.form) return;
    
    SafeDOM.safeQuerySelectorAll('.field-error', this.form).forEach(el => {
      SafeDOM.safeRemoveElement(el);
    });
    
    SafeDOM.safeQuerySelectorAll('input, textarea', this.form).forEach(field => {
      SafeDOM.safeSetStyle(field, { borderColor: '' });
    });
  }

  setLoading(loading) {
    if (!this.isBrowser || !this.submitButton) return;

    if (loading) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Sending...';
      SafeDOM.safeSetStyle(this.submitButton, { opacity: '0.7' });
    } else {
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Send Message';
      SafeDOM.safeSetStyle(this.submitButton, { opacity: '1' });
    }
  }

  resetForm() {
    if (!this.isBrowser || !this.form) return;

    this.form.reset();
    this.clearAllFieldErrors();
    // Messages are on their own auto-dismiss timers — don't clear them here
  }

  // Analytics and utility methods (reused from ContactModal)
  handleAnalytics(eventData) {
    if (!this.options.enableAnalytics) return;
    
    if (this.namespace?.trackClick) {
      this.namespace.trackClick(null, eventData);
    }
    
    if (typeof this.submitterOptions.customAnalytics === 'function') {
      this.submitterOptions.customAnalytics(eventData);
    }
  }

  trackSubmissionEvent(event, data = {}) {
    this.handleAnalytics({
      event: `contact_fixed_form_${event}`,
      component: 'ContactFixedForm',
      ...data
    });
  }

  // Configuration methods
  updateOptions(newOptions) {
    Object.assign(this.options, newOptions);
  }

  updateSubmitterOptions(options) {
    Object.assign(this.submitterOptions, options);
    if (this.formSubmitter) {
      this.formSubmitter = FormSubmitter.createContactSubmitter(this.submitterOptions);
    }
  }

  updateValidationRules(rules) {
    Object.assign(this.validationRules, rules);
    
    if (this.options.debug) {
      console.log('📋 Fixed form validation rules updated:', this.validationRules);
    }
  }

  // Public API methods
  setFormSelector(selector) {
    this.options.formSelector = selector;
  }

  setRedirectUrl(url) {
    this.options.redirectOnSuccess = url;
  }

  setDebugMode(enabled) {
    this.options.debug = enabled;
    this.updateSubmitterOptions({ debug: enabled });
  }

  setEndpoints(endpoints) {
    this.updateSubmitterOptions({ endpoints });
  }

  // Status and diagnostic methods
  getRecentErrors(min = 5) { 
    return this.errorHandler?.getRecentErrors(min) || []; 
  }
  
  hasCriticalIssues() { 
    return this.errorHandler?.hasFatalErrors() || false; 
  }
  
  clearErrorHistory() { 
    this.errorHandler?.clearErrors(); 
  }

  getValidationRules() {
    return { ...this.validationRules };
  }

  getOptions() {
    return { ...this.options };
  }

  destroy() {
    this.removeEventListeners();
    this.eventsBound = false;
    if (this.submitTimeout) clearTimeout(this.submitTimeout);
    
    this.clearAllMessages();
    
    this.errorHandler?.logSuccess('ContactFixedForm destroyed', { 
      category: 'lifecycle', 
      action: 'destroy' 
    });
  }

  isInitialized() { 
    return this.isBrowser && !!this.form; 
  }

  static create(namespace, options = {}) {
    if (!isBrowser) return null;
    return new ContactFixedForm(namespace, options);
  }
}
